import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchError } from "./commonSlice";

const initialState = {
  orders: {
    list: [],
    perPage: 10,
    total: 0,
    orderCount: 0,
    loading: false,
  },
  orderHistory: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  payments: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  productReport: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  statusList: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
};

// Import Orders (CSV)
export const importFlipkartOrders = createAsyncThunk(
  "flipkart/import",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post("flipkart/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Import Payments (Excel)
export const importFlipkartPayments = createAsyncThunk(
  "flipkart/payment/import",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post("flipkart/payment/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Get Import History List
export const getFlipkartOrderHistoryList = createAsyncThunk(
  "flipkart/order-history-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("flipkart/import-history/list", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Get Orders List
export const getFlipkartOrderList = createAsyncThunk(
  "flipkart/order-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("flipkart/orders/list", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Delete Order
export const deleteFlipkartOrder = createAsyncThunk(
  "flipkart/delete-order",
  async (id, { dispatch }) => {
    try {
      const response = await axios.delete(`flipkart/order/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Get Payments List
export const getFlipkartPaymentList = createAsyncThunk(
  "flipkart/payment-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("flipkart/payment/list", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Get Product Report
export const getFlipkartProductReport = createAsyncThunk(
  "flipkart/product-report",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("flipkart/product-report", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Get Statistics
export const getFlipkartStatistics = createAsyncThunk(
  "flipkart/statistics",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("flipkart/statistics", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Get Statistics Status List
export const getFlipkartStatisticsStatusList = createAsyncThunk(
  "flipkart/statistics-status-list",
  async ({ id, data }, { dispatch }) => {
    try {
      const response = await axios.get(`flipkart/${id}/product`, {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Get Finance Dashboard
export const getFlipkartFinanceDashboard = createAsyncThunk(
  "flipkart/finance-dashboard",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("flipkart/finance-dashboard", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

const flipkartSlice = createSlice({
  name: "flipkart",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Orders List
      .addCase(getFlipkartOrderList.pending, (state) => {
        state.orders.loading = true;
      })
      .addCase(getFlipkartOrderList.fulfilled, (state, action) => {
        state.orders.loading = false;
        state.orders.list =
          action?.payload?.data?.data?.flipkartOrder ??
          action?.payload?.data?.data?.orders ??
          [];
        state.orders.perPage = action?.payload?.data?.data?.perPage ?? 10;
        state.orders.total = action?.payload?.data?.data?.total ?? 0;
        state.orders.orderCount = action?.payload?.data?.data?.orderCount ?? 0;
      })
      .addCase(getFlipkartOrderList.rejected, (state) => {
        state.orders.loading = false;
        state.orders.list = [];
        state.orders.perPage = 10;
        state.orders.total = 0;
        state.orders.orderCount = 0;
      })

      // Order History List
      .addCase(getFlipkartOrderHistoryList.pending, (state) => {
        state.orderHistory.loading = true;
      })
      .addCase(getFlipkartOrderHistoryList.fulfilled, (state, action) => {
        state.orderHistory.loading = false;
        state.orderHistory.list =
          action?.payload?.data?.data?.importHistory ?? [];
        state.orderHistory.perPage =
          action?.payload?.data?.data?.perPage ?? 10;
        state.orderHistory.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getFlipkartOrderHistoryList.rejected, (state) => {
        state.orderHistory.loading = false;
        state.orderHistory.list = [];
        state.orderHistory.perPage = 10;
        state.orderHistory.total = 0;
      })

      // Payments List
      .addCase(getFlipkartPaymentList.pending, (state) => {
        state.payments.loading = true;
      })
      .addCase(getFlipkartPaymentList.fulfilled, (state, action) => {
        state.payments.loading = false;
        state.payments.list =
          action?.payload?.data?.data?.payments ??
          action?.payload?.data?.data?.flipkartOrder ??
          action?.payload?.data?.data?.orders ??
          [];
        state.payments.perPage = action?.payload?.data?.data?.perPage ?? 10;
        state.payments.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getFlipkartPaymentList.rejected, (state) => {
        state.payments.loading = false;
        state.payments.list = [];
        state.payments.perPage = 10;
        state.payments.total = 0;
      })

      // Product Report
      .addCase(getFlipkartProductReport.pending, (state) => {
        state.productReport.loading = true;
      })
      .addCase(getFlipkartProductReport.fulfilled, (state, action) => {
        const reportData = action?.payload?.data?.data;
        const reportList =
          reportData?.products ??
          reportData?.paginatedList ??
          reportData?.list ??
          (Array.isArray(reportData) ? reportData : []);
        state.productReport.loading = false;
        state.productReport.list = reportList;
        state.productReport.perPage = reportData?.perPage ?? 10;
        state.productReport.total = reportData?.total ?? reportList.length ?? 0;
      })
      .addCase(getFlipkartProductReport.rejected, (state) => {
        state.productReport.loading = false;
        state.productReport.list = [];
        state.productReport.perPage = 10;
        state.productReport.total = 0;
      })

      // Statistics Status List
      .addCase(getFlipkartStatisticsStatusList.pending, (state) => {
        state.statusList.loading = true;
      })
      .addCase(getFlipkartStatisticsStatusList.fulfilled, (state, action) => {
        state.statusList.loading = false;
        state.statusList.list = action?.payload?.data?.data?.products ?? [];
        state.statusList.perPage = action?.payload?.data?.data?.perPage ?? 10;
        state.statusList.total =
          action?.payload?.data?.data?.totalProduct ?? 0;
      })
      .addCase(getFlipkartStatisticsStatusList.rejected, (state) => {
        state.statusList.loading = false;
        state.statusList.list = [];
        state.statusList.perPage = 10;
        state.statusList.total = 0;
      });
  },
});

export default flipkartSlice.reducer;
